/* eslint-disable @nx/enforce-module-boundaries */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
} from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BaseListItems,
  CardTableComponent,
  ConfirmDialogService,
  CustomPaginator,
  FilterParams,
  LoaderIndicatorDirective,
  PreloadImgComponent,
  SearchComponent,
} from '@t-llevo/common-ui';
import { apiVehicleTypeRoutes as apiRoutes } from '../../../api/routes/api-vehicle-type-routes';
import { VehicleTypesFormComponent } from '../vehicle-types-form/vehicle-types-form.component';
import { VehicleTypesService } from '../../../api/services/vehicle-types.service';
import { VehicleType } from 'admin-drivers/src/app/common/models/VehicleType';
import { VehicleTypeResponse } from 'admin-drivers/src/app/api/models/responses/VehicleTypeResponse';
import { TagComponent } from 'admin-drivers/src/app/common/components/tag.component';
@Component({
  selector: 't-llevo-vehicle-types-list',
  standalone: true,
  imports: [
    CommonModule,
    CardTableComponent,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatMenuModule,
    LoaderIndicatorDirective,
    SearchComponent,
    MatDialogModule,
    PreloadImgComponent,
    MatTooltipModule,
    PreloadImgComponent,
    TagComponent,
  ],
  templateUrl: './vehicle-types-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ConfirmDialogService,
    { provide: MatPaginatorIntl, useValue: CustomPaginator() },
  ],
  styles: `
    .mat-column-photo {
      max-width: 100px;
    }
  `,
})
export class VehicleTypesListComponent
  extends BaseListItems<VehicleType, VehicleTypeResponse[]>
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @Input() searchTerm = '';
  @Output() openDetail = new EventEmitter<VehicleType>();
  displayedColumns: string[] = [
    'photo',
    'name',
    'capacity',
    'combustible',
    'available',
    'actions',
  ];

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  showDrawer = false;
  dialogConfig = new MatDialogConfig();
  destroyRef = inject(DestroyRef);

  paginatedItems: MatTableDataSource<VehicleType> = new MatTableDataSource();

  constructor(
    public dialog: MatDialog,
    private vehicleTypesSrv: VehicleTypesService,
  ) {
    super(vehicleTypesSrv, apiRoutes.routeTypeVehicles);
  }

  ngOnInit(): void {
    this.loadItems(this._getFiltersParams());

    this.paginatedItems.sortingDataAccessor = (item, property) => {
      switch (property) {
        default:
          return item.name;
      }
    };

    this.paginatedItems.filterPredicate = (item, filter) =>
      this.filterItem(item, filter.trim().toLowerCase());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchTerm']) {
      this.onSearch(changes['searchTerm'].currentValue);
    }
  }
  ngAfterViewInit(): void {
    this.dataSource.connect().subscribe((data) => {
      this.paginatedItems.data = data.slice(0, data.length);
      this.paginatedItems.paginator = this.paginator;
      this.paginatedItems.sort = this.sort;
    });
  }

  ngOnDestroy(): void {
    this.stop$.next(true);
    this.stop$.complete();
  }

  openAddVehicleDialog() {
    this.dialog
      .open(VehicleTypesFormComponent, {
        width: '50%',
        maxHeight: '90%',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((data: boolean) => {
        if (data) this.loadItems(this._getFiltersParams());
      });
  }

  openEditVehicleDialog(itemData: VehicleType) {
    this.dialog
      .open(VehicleTypesFormComponent, {
        data: {
          edit: true,
          data: itemData,
        },
        width: '50%',
        maxHeight: '90%',
        disableClose: true,
        panelClass: 'bg-gray',
      })
      .afterClosed()
      .subscribe((data: boolean) => {
        if (data) {
          this.loadItems(this._getFiltersParams());
        }
      });
  }

  openChangeAvailabilityDialog(type: VehicleType) {
    const config = {
      title: `¿Desea ${type.available ? 'deshabilitar' : 'habilitar'} el tipo de vehículo?`,
      message: `Los tipos de vehículos '${type.name}' ${type.available ? 'no' : ''} podrán ser requeridos por los clientes`,
      cancelText: 'CANCELAR',
      confirmText: `${type.available ? 'DESHABILITAR' : 'HABILITAR'}`,
    };
    this.confirmDialogSrv.open(config).subscribe((dataDialog) => {
      if (dataDialog) {
        this.vehicleTypesSrv.changeVehicleTypeAvailability(type).subscribe({
          next: () => {
            this.notifySrv.dispatch(`Tipo de vehículo ${type.available? 'deshabilitado': 'habilitado'}`, 'success');
            this.loadItems(this._getFiltersParams());
          },
        });
      }
    });
  }

  openDeleteDialog(type: VehicleType) {
    const config = {
      title: `¿Desea eliminar el elemento?`,
      message: 'El elemento se eliminará de forma permanente',
      cancelText: 'CANCELAR',
      confirmText: 'SI, DESEO ELIMINAR',
    };
    this.confirmDialogSrv.open(config).subscribe((dataDialog) => {
      if (dataDialog) {
        this.vehicleTypesSrv.deleteVehicleTypeById(type.id).subscribe({
          next: () => {
            this.notifySrv.dispatch('Tipo de vehículo eliminado', 'success');
            this.loadItems(this._getFiltersParams());
          },
        });
      }
    });
  }

  _getFiltersParams(defaultField = 'name'): FilterParams {
    const activeField =
      this.sort && this.sort.active ? this.sort.active : defaultField;
    const params: FilterParams = {
      // page: this.paginator ? this.paginator.pageIndex + 1 : 1,
      // page_size: this.paginator ? this.paginator.pageSize : 10,
      // ordering:
      //   this.sort && this.sort.direction === 'asc'
      //     ? activeField
      //     : `-${activeField}`,
      only_available: false,
    };
    if (this.searchParam) {
      params['search'] = this.searchParam;
    }

    return params;
  }

  override onSearch(ev: string): void {
    if (this.searchParam !== ev || this.searchParam === '') {
      this.searchParam = ev;
      this.paginatedItems.filter = this.searchParam;
      //this.loadItems(this._getFiltersParams(), apiRoutes.routeTypeVehicles);
    }
  }

  openedDrawerChange(open: boolean) {
    this.showDrawer = open;
  }

  private filterItem(item: VehicleType, search: string): boolean {
    const searchValue = search.toLowerCase();
    if (
      item.name.toLowerCase().includes(searchValue) ||
      item.passengers.toString().toLowerCase().includes(searchValue) ||
      item.energySource.toLowerCase().includes(searchValue)
    ) {
      return true;
    }

    return false;
  }
}
